import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { CompassColor } from "@noom/wax-component-library";

const BASE_FONT_SIZE = 18;
const MIN_FONT_SIZE = 10;

// Dynamically shrink the medication name only when it would otherwise be
// truncated. We measure the un-shrunk pixel width (by temporarily forcing the
// base font size) and compare against the slot's available width — if it
// doesn't fit, scale linearly toward MIN_FONT_SIZE. Keeps short names like
// "Wegovy® Pill" at the design font size while letting long brand names like
// "Zepbound® Single-Use Vials" remain fully visible across viewport sizes.
function useFitFontSize(text: string) {
  const nameRef = useRef<HTMLElement>(null);
  const [fontSize, setFontSize] = useState(BASE_FONT_SIZE);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return undefined;

    const fit = () => {
      const prevInline = el.style.fontSize;
      el.style.fontSize = `${BASE_FONT_SIZE}px`;
      const naturalWidth = el.scrollWidth;
      const available = el.clientWidth;
      el.style.fontSize = prevInline;

      if (available === 0 || naturalWidth === 0) return;
      if (naturalWidth <= available) {
        setFontSize(BASE_FONT_SIZE);
        return;
      }
      setFontSize(
        Math.max(
          MIN_FONT_SIZE,
          Math.floor((available / naturalWidth) * BASE_FONT_SIZE)
        )
      );
    };

    fit();

    const RO = window.ResizeObserver;
    if (typeof RO === "undefined") return undefined;
    const ro = new RO(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  return { nameRef, fontSize };
}

/**
 * Visual treatment for the medication card header.
 *  - `prominent`: full-opacity teal/green gradient — used as a top-of-page
 *    header (e.g. PrescriptionFulfillmentDetailPage).
 *  - `subtle`: 40% gradient over white — used inline in a content body
 *    (e.g. ConfirmRefillPage).
 */
export type MedicationCardHeaderVariant = "prominent" | "subtle";

export type MedicationCardHeaderProps = {
  /** Display name shown on the left (e.g. "Wegovy® Pill"). */
  name: string;
  /**
   * URL of the medication image (typically `prescription_catalog.drug_display_url`).
   * Renders nothing when missing or not an https URL.
   */
  imageUrl?: string | null;
  /** Alt text for the image. Defaults to `name`. */
  imageAlt?: string;
  /** Visual treatment. Defaults to `subtle`. */
  variant?: MedicationCardHeaderVariant;
  /**
   * If true, the image overhangs the top-right corner of the card (used in the
   * prominent header treatment). If false, the image is contained inside the
   * card on the right.
   */
  imageOverflow?: boolean;
  /** Optional CSS `transform` applied to the image (e.g. "rotate(15deg)"). */
  imageTransform?: string;
  /**
   * Optional content rendered to the right of the medication name — typically
   * an info icon (decorative) or info button (navigates to drug info).
   */
  info?: React.ReactNode;
  className?: string;
};

const PROMINENT_GRADIENT = "linear-gradient(to right, #c8e3b1, #6cc1b6)";
const SUBTLE_GRADIENT = `linear-gradient(
  90deg,
  rgba(200, 227, 177, 0.4) 0%,
  rgba(108, 193, 182, 0.4) 100%
)`;

const Card = styled.div<{
  variant: MedicationCardHeaderVariant;
  imageOverflow: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  min-height: 72px;
  padding: 0 16px;
  border-radius: 12px;
  overflow: ${({ imageOverflow }) => (imageOverflow ? "visible" : "hidden")};
  background: ${({ variant }) =>
    variant === "prominent"
      ? PROMINENT_GRADIENT
      : `${SUBTLE_GRADIENT}, ${CompassColor.white}`};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Name = styled.span<{ fontSize: number }>`
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: 700;
  line-height: 1.22;
  color: #191717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ContainedImage = styled.img<{ transform?: string }>`
  width: 74px;
  height: 72px;
  object-fit: contain;
  margin-left: auto;
  flex-shrink: 0;
  ${({ transform }) => (transform ? `transform: ${transform};` : "")}
`;

const OverflowImage = styled.img<{ transform?: string }>`
  position: absolute;
  right: -2px;
  top: 50%;
  width: 72px;
  height: 72px;
  object-fit: contain;
  transform: ${({ transform }) =>
    transform ? `translateY(-50%) ${transform}` : "translateY(-50%)"};
`;

const getSafeImageUrl = (url: string | null | undefined): string | null => {
  if (typeof url !== "string") return null;

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "https:" ? parsedUrl.href : null;
  } catch {
    return null;
  }
};

export const MedicationCardHeader: React.FC<MedicationCardHeaderProps> = ({
  name,
  imageUrl,
  imageAlt,
  variant = "subtle",
  imageOverflow = false,
  imageTransform,
  info,
  className,
}) => {
  const safeImageUrl = getSafeImageUrl(imageUrl);
  const ImageComponent = imageOverflow ? OverflowImage : ContainedImage;
  const { nameRef, fontSize } = useFitFontSize(name);

  return (
    <Card variant={variant} imageOverflow={imageOverflow} className={className}>
      <NameRow>
        <Name ref={nameRef} fontSize={fontSize}>
          {name}
        </Name>
        {info}
      </NameRow>
      {safeImageUrl && (
        <ImageComponent
          src={safeImageUrl}
          alt={imageAlt ?? name}
          transform={imageTransform}
          onError={(e: React.SyntheticEvent<HTMLElement>) => {
            const image = e.currentTarget;
            image.hidden = true;
          }}
        />
      )}
    </Card>
  );
};
